/**
 * Emerging Skills Component
 * Shows trending skills with growth rates
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import careerService from '../../services/careerService';
import './EmergingSkills.css';

const EmergingSkills = ({ industry = 'Technology', onSkillClick }) => {
  const navigate = useNavigate();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIndustry, setSelectedIndustry] = useState(industry);
  const [timeframe, setTimeframe] = useState('12months');

  const industries = [
    'Technology',
    'Renewable Energy',
    'Healthcare',
    'Finance',
    'Manufacturing',
    'Education',
    'Agriculture'
  ];

  useEffect(() => {
    loadEmergingSkills();
  }, [selectedIndustry, timeframe]);

  const loadEmergingSkills = async () => {
    try {
      setLoading(true);
      const data = await careerService.getEmergingSkills(selectedIndustry, timeframe, 10);
      setSkills(data || []);
      setError(null);
    } catch (err) {
      console.error('Error loading emerging skills:', err);
      setError('Failed to load trending skills');
    } finally {
      setLoading(false);
    }
  };

  const getGrowthColor = (rate) => {
    if (rate >= 50) return '#4CAF50';
    if (rate >= 30) return '#8BC34A';
    if (rate >= 20) return '#FFC107';
    return '#FF9800';
  };

  const getTrendIcon = (rate) => {
    if (rate >= 50) return 'fa-rocket';
    if (rate >= 30) return 'fa-arrow-trend-up';
    return 'fa-arrow-up';
  };

  const formatSalary = (amount) => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    }
    return `$${amount}`;
  };

  if (loading) {
    return (
      <div className="emerging-skills">
        <div className="skills-loading">
          <div className="spinner"></div>
          <p>Loading trending skills...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emerging-skills">
      {/* Header */}
      <div className="skills-header">
        <div className="header-content">
          <h2>
            <i className="fas fa-rocket"></i>
            Emerging Skills
          </h2>
          <p>Skills with the highest growth in demand</p>
        </div>
        <div className="header-controls">
          <select
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
            className="industry-select"
          >
            {industries.map((ind) => (
              <option key={ind} value={ind}>
                {ind}
              </option>
            ))}
          </select>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            className="timeframe-select"
          >
            <option value="3months">3 Months</option>
            <option value="6months">6 Months</option>
            <option value="12months">12 Months</option>
          </select>
        </div>
      </div>

      {/* Skills List */}
      {error ? (
        <div className="skills-error">
          <i className="fas fa-exclamation-circle"></i>
          <p>{error}</p>
        </div>
      ) : skills.length === 0 ? (
        <div className="skills-empty">
          <i className="fas fa-info-circle"></i>
          <p>No trending skills found for {selectedIndustry}</p>
        </div>
      ) : (
        <div className="skills-list">
          {skills.map((skill, index) => (
            <div key={index} className="skill-item">
              {/* Rank Badge */}
              <div className="skill-rank">
                <span className={`rank-badge rank-${index + 1}`}>
                  #{index + 1}
                </span>
              </div>

              {/* Skill Info */}
              <div className="skill-info">
                <h3 className="skill-name">{skill.skill}</h3>
                <div className="skill-stats">
                  <span className="stat">
                    <i className="fas fa-briefcase"></i>
                    {skill.jobCount?.toLocaleString()} jobs
                  </span>
                  {skill.avgSalary && (
                    <span className="stat">
                      <i className="fas fa-dollar-sign"></i>
                      {formatSalary(skill.avgSalary)} avg
                    </span>
                  )}
                  <span className="stat demand">
                    <i className="fas fa-fire"></i>
                    Demand: {skill.currentDemand}/100
                  </span>
                </div>
                {skill.topCompanies && skill.topCompanies.length > 0 && (
                  <div className="skill-companies">
                    <i className="fas fa-building"></i>
                    {skill.topCompanies.slice(0, 3).join(', ')}
                  </div>
                )}
              </div>

              {/* Growth Indicator */}
              <div className="skill-growth">
                <i 
                  className={`fas ${getTrendIcon(skill.growthRate)}`}
                  style={{ color: getGrowthColor(skill.growthRate) }}
                ></i>
                <div className="growth-info">
                  <span 
                    className="growth-rate"
                    style={{ color: getGrowthColor(skill.growthRate) }}
                  >
                    +{skill.growthRate.toFixed(1)}%
                  </span>
                  <span className="growth-label">growth</span>
                </div>
              </div>

              {/* Actions */}
              <div className="skill-actions">
                <button
                  className="btn-analyze"
                  onClick={() => onSkillClick && onSkillClick(skill.skill)}
                  title="View detailed analysis"
                >
                  <i className="fas fa-chart-line"></i>
                </button>
                <button
                  className="btn-add"
                  onClick={() => navigate(`/create-path?skill=${encodeURIComponent(skill.skill)}`)}
                  title="Add to learning path"
                >
                  <i className="fas fa-plus"></i>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View All Button */}
      {skills.length > 0 && (
        <div className="skills-footer">
          <button
            className="btn-view-all"
            onClick={() => navigate('/career/emerging-skills')}
          >
            View All Trending Skills
            <i className="fas fa-arrow-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default EmergingSkills;
